import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, CheckCircle2, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { useAuth } from "@/features/auth/useAuth";
import {
  listKpiActions,
  saveKpiAction,
  type KpiAction,
} from "@/features/kpi/actions";
import { canCollectKpis, isWeeklyOpsKpi, visibleKpiDefs } from "@/features/kpi/access";
import {
  KpiBackendNotice,
  KpiPeriodToolbar,
  KpiTable,
} from "@/features/kpi/components";
import {
  KPI_DEFS,
  SCORECARD_SECTIONS,
  kpiOnTarget,
} from "@/features/kpi/definitions";
import { buildKpiCsv, downloadKpiCsv } from "@/features/kpi/export";
import { useKpiWorkspace } from "@/features/kpi/useKpiWorkspace";

type DivisionFilter = "all" | "company" | "tanker" | "dryvan";

const SCORECARD_FILTER_SECTIONS: Record<
  Exclude<DivisionFilter, "all">,
  Set<string>
> = {
  company: new Set(["company", "accounting", "fmcsa"]),
  tanker: new Set(["tanker-ops", "tanker-safety", "fleet-liquid", "sales", "fmcsa"]),
  dryvan: new Set(["dryvan-ops", "fleet-dry"]),
};

export default function InternalKpiScorecard() {
  const { user } = useAuth();
  const workspace = useKpiWorkspace();
  const currentEntries = workspace.current;
  const [division, setDivision] = useState<DivisionFilter>("all");
  const [redOnly, setRedOnly] = useState(false);
  const [opsReview, setOpsReview] = useState(false);
  const allowedDefs = useMemo(() => visibleKpiDefs(user), [user]);
  const canCollect = canCollectKpis(user);
  const [actions, setActions] = useState<KpiAction[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionDraft, setActionDraft] = useState({
    kpiId: "",
    issue: "",
    owner: "",
    dueDate: "",
  });

  const refreshActions = useCallback(async () => {
    try {
      const allowed = new Set(visibleKpiDefs(user).map((def) => def.id));
      setActions(
        (await listKpiActions(workspace.periodType, workspace.periodStart)).filter((action) =>
          allowed.has(action.kpiId),
        ),
      );
      setActionError(null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not load action items.");
    }
  }, [user, workspace.periodStart, workspace.periodType]);

  useEffect(() => {
    void refreshActions();
  }, [refreshActions]);

  useEffect(() => {
    setOpsReview(workspace.periodType === "week");
  }, [workspace.periodType]);

  const scorecardDefs = useMemo(
    () =>
      allowedDefs.filter((def) => {
        if (!def.scorecardSection) return false;
        if (opsReview && !isWeeklyOpsKpi(def)) return false;
        if (
          division !== "all" &&
          !SCORECARD_FILTER_SECTIONS[division].has(def.scorecardSection)
        ) {
          return false;
        }
        if (redOnly) {
          const entry = currentEntries.get(def.id);
          return Boolean(entry && kpiOnTarget(def, entry.value) === false);
        }
        return true;
      }),
    [allowedDefs, currentEntries, division, opsReview, redOnly],
  );

  const offTargetDefs = allowedDefs.filter((def) => {
    const entry = currentEntries.get(def.id);
    return entry && kpiOnTarget(def, entry.value) === false;
  });

  async function addAction() {
    if (!actionDraft.kpiId || !actionDraft.issue.trim()) return;
    try {
      await saveKpiAction(
        {
          kpiId: actionDraft.kpiId,
          periodType: workspace.periodType,
          periodStart: workspace.periodStart,
          issue: actionDraft.issue.trim(),
          actionOwner: actionDraft.owner.trim(),
          dueDate: actionDraft.dueDate,
          status: "open",
        },
        user?.id,
      );
      setActionDraft({ kpiId: "", issue: "", owner: "", dueDate: "" });
      await refreshActions();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not save action item.");
    }
  }

  async function markDone(action: KpiAction) {
    await saveKpiAction({ ...action, status: "done" }, user?.id);
    await refreshActions();
  }

  return (
    <>
      <Seo title="KPI Scorecard" noindex />
      <DemoBanner />
      <Link
        to="/app/internal/kpis"
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-brand-500 hover:text-brand-900"
      >
        <ArrowLeft className="h-4 w-4" />
        KPI management
      </Link>
      <div>
        <h1 className="text-2xl font-bold">Executive Scorecard</h1>
        <p className="mt-1 max-w-3xl text-sm text-brand-500">
          A read-only executive view of the same Framework values, regrouped by
          division. Enter or override values from the department detail pages.
        </p>
      </div>
      <KpiBackendNotice backend={workspace.backend} />
      <KpiPeriodToolbar
        periodType={workspace.periodType}
        periodStart={workspace.periodStart}
        onPeriodType={workspace.switchPeriodType}
        onPeriodStart={workspace.setPeriodStart}
        entered={scorecardDefs.filter((def) => workspace.current.has(def.id)).length}
        total={scorecardDefs.length}
        dirtyCount={0}
        saving={false}
        saved={false}
        onSave={() => undefined}
        showSave={false}
        collecting={workspace.collecting}
        onCollect={canCollect ? (force) => void workspace.collectAuto(force) : undefined}
        lastCollectAt={workspace.lastCollectAt}
        collectFresh={workspace.collectFresh}
        onExport={() =>
          downloadKpiCsv(
            `kpi-scorecard-${workspace.periodType}-${workspace.periodStart}.csv`,
            buildKpiCsv(
              scorecardDefs,
              workspace.current,
              workspace.previous,
              workspace.periodType,
              workspace.periodStart,
            ),
          )
        }
      />
      {workspace.collectNote && (
        <FeedbackMessage tone="info" className="mt-4">{workspace.collectNote}</FeedbackMessage>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {([
          ["all", "All"],
          ["company", "Company"],
          ["tanker", "Liquid Tanker"],
          ["dryvan", "Dry Van"],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setDivision(value)}
            className={
              "rounded-full border px-3 py-1.5 text-sm font-medium " +
              (division === value
                ? "border-brand-900 bg-brand-900 text-white"
                : "border-brand-200 bg-white text-brand-600 hover:bg-brand-50")
            }
          >
            {label}
          </button>
        ))}
        <label className="ml-2 inline-flex cursor-pointer items-center gap-2 text-sm text-brand-600">
          <input
            type="checkbox"
            checked={redOnly}
            onChange={(event) => setRedOnly(event.target.checked)}
            className="h-4 w-4 rounded border-brand-300"
          />
          Red KPIs only
        </label>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-brand-600">
          <input
            type="checkbox"
            checked={opsReview}
            onChange={(event) => setOpsReview(event.target.checked)}
            className="h-4 w-4 rounded border-brand-300"
          />
          Weekly ops subset
        </label>
      </div>

      {workspace.error && (
        <FeedbackMessage tone="error" className="mt-4">{workspace.error}</FeedbackMessage>
      )}
      {workspace.loading ? (
        <div className="mt-6"><CardSkeleton rows={6} /></div>
      ) : (
        <div className="mt-6 grid gap-6">
          {SCORECARD_SECTIONS.map((section) => {
            const definitions = scorecardDefs.filter(
              (def) => def.scorecardSection === section.id,
            );
            if (!definitions.length) return null;
            return (
              <Card key={section.id}>
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                  <p className="text-sm text-brand-500">{section.blurb}</p>
                </CardHeader>
                <CardContent>
                  <KpiTable
                    definitions={definitions}
                    current={workspace.current}
                    previous={workspace.previous}
                    allEntries={workspace.entries}
                    draft={workspace.draft}
                    onDraftChange={() => undefined}
                    dirtyIds={[]}
                    readOnly
                    periodType={workspace.periodType}
                    periodStart={workspace.periodStart}
                    userId={user?.id}
                  />
                </CardContent>
              </Card>
            );
          })}
          {!scorecardDefs.length && (
            <FeedbackMessage tone="info">
              No off-target KPI with a numeric threshold matches this filter.
            </FeedbackMessage>
          )}
          <ActionItems
            actions={actions}
            offTargetDefs={offTargetDefs}
            draft={actionDraft}
            onDraft={setActionDraft}
            onAdd={() => void addAction()}
            onDone={(action) => void markDone(action)}
          />
          <Card>
            <CardHeader><CardTitle>Review rules</CardTitle></CardHeader>
            <CardContent className="text-sm text-brand-600">
              <ul className="list-disc space-y-1 pl-5">
                <li>Weekly: abbreviated operations review (company + tank/van dispatch + fleet). Toggle off to see the full Scorecard.</li>
                <li>Monthly: full ownership and leadership review.</li>
                <li>Trends matter more than one-off variance.</li>
                <li>Every persistent red KPI gets an owner, cause, and due date.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
      {actionError && (
        <FeedbackMessage tone="error" className="mt-4">{actionError}</FeedbackMessage>
      )}
    </>
  );
}

function ActionItems({
  actions,
  offTargetDefs,
  draft,
  onDraft,
  onAdd,
  onDone,
}: {
  actions: KpiAction[];
  offTargetDefs: typeof KPI_DEFS;
  draft: { kpiId: string; issue: string; owner: string; dueDate: string };
  onDraft: (draft: { kpiId: string; issue: string; owner: string; dueDate: string }) => void;
  onAdd: () => void;
  onDone: (action: KpiAction) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Executive notes &amp; action items</CardTitle>
        <p className="text-sm text-brand-500">Scorecard section 7: assign every red KPI a fix owner and due date.</p>
      </CardHeader>
      <CardContent>
        {actions.length > 0 && (
          <div className="mb-5 grid gap-2">
            {actions.map((action) => {
              const def = KPI_DEFS.find((item) => item.id === action.kpiId);
              return (
                <div key={action.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-brand-100 p-3">
                  <Badge variant={action.status === "done" ? "success" : "danger"}>
                    {action.status === "done" ? "Done" : "Open"}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{def?.name ?? action.kpiId}</div>
                    <div className="text-sm text-brand-500">{action.issue}</div>
                  </div>
                  <div className="text-xs text-brand-400">
                    {action.actionOwner || "Unassigned"}{action.dueDate ? ` · due ${action.dueDate}` : ""}
                  </div>
                  {action.status === "open" && (
                    <Button variant="outline" size="sm" onClick={() => onDone(action)}>
                      <CheckCircle2 className="mr-1.5 h-4 w-4" /> Done
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="grid gap-3 lg:grid-cols-[1.2fr_2fr_1fr_1fr_auto] lg:items-end">
          <div>
            <Label htmlFor="action-kpi">Red KPI</Label>
            <select
              id="action-kpi"
              value={draft.kpiId}
              onChange={(event) => onDraft({ ...draft, kpiId: event.target.value })}
              className="h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
            >
              <option value="">Select KPI</option>
              {offTargetDefs.map((def) => <option key={def.id} value={def.id}>{def.name}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="action-issue">Issue / corrective action</Label>
            <Input id="action-issue" value={draft.issue} onChange={(event) => onDraft({ ...draft, issue: event.target.value })} />
          </div>
          <div>
            <Label htmlFor="action-owner">Owner</Label>
            <Input id="action-owner" value={draft.owner} onChange={(event) => onDraft({ ...draft, owner: event.target.value })} />
          </div>
          <div>
            <Label htmlFor="action-date">Due date</Label>
            <Input id="action-date" type="date" value={draft.dueDate} onChange={(event) => onDraft({ ...draft, dueDate: event.target.value })} />
          </div>
          <Button onClick={onAdd} disabled={!draft.kpiId || !draft.issue.trim()}>
            <Plus className="mr-1.5 h-4 w-4" /> Add
          </Button>
        </div>
        {!offTargetDefs.length && !actions.length && (
          <p className="mt-3 text-sm text-brand-400">No off-target KPI has been recorded for this period yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
